import { app } from '../../app.module'
/**
 * タグ管理用の共通サービス
 * @param  {Function} Restangular Restangular
 * @param  {Function} $q Promise用
 * @param  {Function} $rootScope rootScope
 * @return {Object} オブジェクト
 */
function EditTagCommon(Restangular, $q, $rootScope, $state, AccountSetData) {
  /**
   * タグのサジェストのため、グループ内の全てのタグを取得
   * @param  tagType タグ種類
   * @param  tagRole タグの役割(グローバル、グループ、ユーザー)
   * @param  snsType SNS種類
   * @param  groupId グループID
   * @param  userId ユーザーID
   * @return グループ内のすべてのタグ
   */
  function getAllTags(
    tagType: string,
    tagRole: string,
    snsType: string,
    groupId: number,
    userId: number
  ) {
    // Promise用にdeferredオブジェクトを作成
    const deferred = $q.defer()
    const getUrl = '/tag/getAllTags'
    const params = `?tag_type=${tagType}&tag_role=${tagRole}&sns_type=${snsType}&group_id=${groupId}&user_id=${userId}`
    Restangular.all(getUrl + params)
      .getList()
      .then((result) => {
        deferred.resolve(result[0].data)
      })
    // promiseの結果を返す
    return deferred.promise
  }

  /**
   * 新規追加したタグが、すでに追加済みのタグの場合は追加させないようにする
   * @param  {Object} newTag 新規追加予定のタグ
   * @param  {Object} tags 対象につけられたタグ一覧
   * @return {Object} グループ内のすべてのタグ
   */
  function checkTagExist(
    newTag: {
      tagName: string
    },
    tags
  ) {
    // 重複チェックのフラグ
    let flag = 0

    // 入力されたタグが重複して存在するかチェック
    tags.forEach((element) => {
      if (element.tagName === newTag.tagName) {
        flag = flag + 1
      }
    })
    // 入力されたタグが、すでに同一投稿タグ内に存在する場合
    if (flag > 0) {
      // タグの追加を行わない
      return null
      // タグの重複がない場合は、新規追加タグをそのまま返す
    } else {
      return newTag
    }
  }

  /**
   * タグの追加、削除があった場合は、投稿一覧、アカウント一覧へ通知する
   * @param  {string} tagRole タグの役割(グローバル、グループ、ユーザー)
   * @param  {number} targetId 投稿ID,もしくはアカウントID
   * @param  {object} newPostTags 変更後の投稿タグ一覧
   * @return {Object} グループ内のすべてのタグ
   */
  function sendToTable(tagRole: string, targetId: number, newTags: {}[]) {
    // 変更のあったディレクティブにのみイベント送信するため、ターゲットIDをイベント名に含める
    const eventName = 'changeTag_' + targetId
    const sendData = {
      tagRole: tagRole,
      newTags: newTags,
    }

    // タグ一覧のディレクティブにイベントをemitする
    $rootScope.$emit(eventName, sendData)
  }

  /**
   * 対象につけられたタグを取得する
   * @param  tagRole タグの役割(グローバル、グループ、ユーザー)
   * @param  snsType SNS種類
   * @param  groupId グループID
   * @param  userId ユーザーID
   * @param  targetId FB投稿ID
   * @return グループ内のすべてのタグ
   */
  function getTags(
    tagType: string,
    tagRole: string,
    snsType: string,
    groupId: number,
    userId: number,
    targetId: number
  ) {
    // Promise用にdeferredオブジェクトを作成
    const deferred = $q.defer()
    const getUrl = '/tag/getTagMap'
    const params = `?tag_type=${tagType}&tag_role=${tagRole}&sns_type=${snsType}&group_id=${groupId}&user_id=${userId}&target_id=${targetId}`

    Restangular.all(getUrl + params)
      .getList()
      .then((result) => {
        deferred.resolve(result[0].data)
      })
    // promiseの結果を返す
    return deferred.promise
  }

  function getAllTagMap(
    tagType: string,
    tagRole: string,
    snsType: string,
    groupId: number,
    userId: number,
    targetIds: number[]
  ) {
    const requests = targetIds.map((targetId) => {
      return getTags(tagType, tagRole, snsType, groupId, userId, targetId)
    })

    return $q.all(requests)
  }

  /**
   * タグを付ける
   * @param tagRole タグの役割(グローバル、グループ、ユーザー)
   * @param groupId グループID
   * @param userId ユーザーID
   * @param targetId FB投稿ID
   * @param tagName 投稿タグ名
   * @return サーバーからの結果
   */
  function addTag(
    tagType: string,
    tagRole: string,
    snsType: string,
    groupId: number,
    userId: number,
    targetId: number,
    tagName: string
  ) {
    // Promise用にdeferredオブジェクトを作成
    const deferred = $q.defer()
    // POST用のデータをセット
    const postData = {
      tagType: tagType,
      tagRole: tagRole,
      snsType: snsType,
      groupId: groupId,
      userId: userId,
      targetId: targetId,
      newTag: tagName,
    }

    const postUrl = '/tag/addTag'

    Restangular.all(postUrl)
      .post(postData)
      .then((result) => {
        // エラーがない場合
        if (result[0].data.error === undefined) {
          // アカウント画面 and 追加されたタグがまだ存在しない場合、追加
          if (
            $state.current !== undefined &&
            $state.current.name === 'account' &&
            AccountSetData.accountTags.indexOf(tagName) === -1
          ) {
            AccountSetData.accountTags.push(tagName)
          }

          deferred.resolve(result[0].data)
        }
      })
    // promiseの結果を返す
    return deferred.promise
  }

  /**
   * 一括でタグを付ける
   * @param tagRole タグの役割(グローバル、グループ、ユーザー)
   * @param groupId グループID
   * @param userId ユーザーID
   * @param targetIds 対象のIDの配列
   * @param tagName タグ名
   * @return サーバーからの結果
   */
  function addTagByBatch(
    tagType: string,
    tagRole: string,
    snsType: string,
    groupId: number,
    userId: number,
    targetIds: number,
    tagName: string
  ) {
    // Promise用にdeferredオブジェクトを作成
    const deferred = $q.defer()
    // POST用のデータをセット
    const postData = {
      tagType: tagType,
      tagRole: tagRole,
      snsType: snsType,
      groupId: groupId,
      userId: userId,
      targetIds: targetIds,
      newTag: tagName,
    }

    const postUrl = '/tag/addTagByBatch'

    Restangular.all(postUrl)
      .post(postData)
      .then((result) => {
        // エラーがない場合
        if (result[0].data.error === undefined) {
          // 取得した最初の配列を返す
          deferred.resolve(result[0].data[0])
        }
      })
    // promiseの結果を返す
    return deferred.promise
  }

  /**
   * タグを削除する
   * @param tagRole タグの役割(グローバル、グループ、ユーザー)
   * @param snsType SNSの種類
   * @param groupId グループID
   * @param userId ユーザーID
   * @param targetId FB投稿ID
   * @param tagId 投稿タグID
   * @return サーバーからの結果
   */
  function deleteTag(
    tagType: string,
    tagRole: string,
    snsType: string,
    targetIds: string[],
    tagId: number
  ) {
    if (typeof targetIds === 'number' || typeof targetIds === 'string') {
      targetIds = [String(targetIds)]
    }
    // Promise用にdeferredオブジェクトを作成
    const deferred = $q.defer()
    // POST用のデータをセット
    const postData = {
      tagType: tagType,
      tagRole: tagRole,
      snsType: snsType,
      targetIds: targetIds,
      tagId: tagId,
    }

    const postUrl = '/tag/deleteTag'

    Restangular.all(postUrl)
      .post(postData)
      .then((result) => {
        if (result[0].data.error === undefined) {
          deferred.resolve(result)
        }
      })
    // promiseの結果を返す
    return deferred.promise
  }

  return {
    getAllTags: getAllTags,
    checkTagExist: checkTagExist,
    sendToTable: sendToTable,
    getTags: getTags,
    getAllTagMap: getAllTagMap,
    addTag: addTag,
    addTagByBatch: addTagByBatch,
    deleteTag: deleteTag,
  }
}

/**
 * 投稿タグ管理モーダル用のサービスを1つのサービスにオブジェクトとして格納
 * @param {Function} EditTagCommon 投稿タグ管理用の共通サービス
 * @return {Object} サービスのオブジェクト
 */
function TagChipsService(EditTagCommon) {
  return {
    // 投稿タグ管理用の共通サービス
    Common: EditTagCommon,
  }
}

// 投稿タグ管理モーダル用のサービスを定義
app.factory('EditTagCommon', EditTagCommon).factory('TagChipsService', TagChipsService)
